import React, { useState } from "react";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import Badge from "@cloudscape-design/components/badge";
import Header from "@cloudscape-design/components/header";
import Modal from "@cloudscape-design/components/modal";
import TextFilter from "@cloudscape-design/components/text-filter";
import Pagination from "@cloudscape-design/components/pagination";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import { useTranslation } from "react-i18next";
import { getPreference, setPreference } from "../preferences";

export default function FeedsList({ feeds, loading, onEdit, onDelete, onRefresh, onRegenerateRss, onTestSlack, onToggleEnabled, isPrimaryAdmin, onShowError }) {
  const { t } = useTranslation();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [feedsToDelete, setFeedsToDelete] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [filteringText, setFilteringText] = useState("");

  // Sorting state
  const [sortingColumn, setSortingColumn] = useState(() => getPreference("feedsListSortingColumn", { sortingField: "name" }));
  const [sortingDescending, setSortingDescending] = useState(() => getPreference("feedsListSortingDescending", false));

  // Pagination state
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(() => getPreference("feedsListPageSize", 20));

  // Column widths state
  const [columnWidths, setColumnWidths] = useState(() => getPreference("feedsListColumnWidths", {}));

  // Handle sorting change
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(detail.isDescending);
    setPreference("feedsListSortingColumn", detail.sortingColumn);
    setPreference("feedsListSortingDescending", detail.isDescending);
  };

  // Handle column resize
  const handleColumnWidthsChange = ({ detail }) => {
    setColumnWidths(detail.widths);
    setPreference("feedsListColumnWidths", detail.widths);
  };

  // Handle preferences change
  const handlePreferencesConfirm = ({ detail }) => {
    setPageSize(detail.pageSize);
    setPreference("feedsListPageSize", detail.pageSize);
  };

  const handleDeleteClick = () => {
    setFeedsToDelete(selectedItems);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    setActionInProgress(true);
    try {
      for (const feed of feedsToDelete) {
        await onDelete(feed.feed_id);
      }
      setSelectedItems([]);
    } finally {
      setActionInProgress(false);
      setDeleteModalVisible(false);
      setFeedsToDelete([]);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setFeedsToDelete([]);
  };

  const handleEnableDisable = async (enable) => {
    setActionInProgress(true);
    try {
      for (const feed of selectedItems) {
        await onToggleEnabled(feed.feed_id, enable);
      }
      setSelectedItems([]);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRegenerateMultiple = async () => {
    setActionInProgress(true);
    try {
      for (const feed of selectedItems) {
        await onRegenerateRss(feed.feed_id);
      }
      setSelectedItems([]);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleTestSlack = async () => {
    if (selectedItems.length !== 1) return;

    const feed = selectedItems[0];
    console.log("Selected feed for test:", JSON.stringify(feed, null, 2));
    const hasSlackConfig = feed.delivery?.slack_webhook || feed.delivery?.slack_channel_id;
    console.log("Has Slack config:", hasSlackConfig, "webhook:", feed.delivery?.slack_webhook, "channel_id:", feed.delivery?.slack_channel_id);

    if (!hasSlackConfig) {
      if (onShowError) {
        onShowError(t("feeds.error.no-slack-config"));
      }
      return;
    }

    setActionInProgress(true);
    try {
      await onTestSlack(feed.feed_id);
      setSelectedItems([]);
    } finally {
      setActionInProgress(false);
    }
  };

  // Filter feeds based on search text
  const filteredFeeds = feeds.filter((feed) => {
    if (!filteringText) return true;
    const searchText = filteringText.toLowerCase();
    return (
      feed.name?.toLowerCase().includes(searchText) ||
      feed.description?.toLowerCase().includes(searchText) ||
      feed.user_id?.toLowerCase().includes(searchText) ||
      feed.delivery?.channel_name?.toLowerCase().includes(searchText)
    );
  });

  // Sort feeds
  const sortedFeeds = [...filteredFeeds].sort((a, b) => {
    const field = sortingColumn.sortingField || "name";
    let aValue = a[field];
    let bValue = b[field];

    // Handle special cases
    if (field === "news_items_sent") {
      aValue = aValue || 0;
      bValue = bValue || 0;
      const comparison = aValue - bValue;
      return sortingDescending ? -comparison : comparison;
    }

    // String comparison
    aValue = String(aValue || "");
    bValue = String(bValue || "");
    const comparison = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: "base" });
    return sortingDescending ? -comparison : comparison;
  });

  // Paginate
  const paginatedFeeds = sortedFeeds.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize);

  return (
    <>
      <Modal
        visible={deleteModalVisible}
        onDismiss={handleDeleteCancel}
        header={t("feeds.modal.delete-title")}
        closeAriaLabel={t("feeds.modal.close")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDeleteCancel}>
                {t("feeds.action.cancel")}
              </Button>
              <Button variant="primary" onClick={handleDeleteConfirm}>
                {t("feeds.action.delete")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {t("feeds.modal.delete-message")} {feedsToDelete.length}{" "}
        {feedsToDelete.length > 1 ? t("feeds.modal.delete-message-plural") : t("feeds.modal.delete-message-singular")}
        {feedsToDelete.length <= 3 && (
          <Box margin={{ top: "s" }}>
            <ul>
              {feedsToDelete.map((feed) => (
                <li key={feed.feed_id}>{feed.name}</li>
              ))}
            </ul>
          </Box>
        )}
      </Modal>
      <Table
        loading={loading}
        loadingText={t("feeds.list.loading")}
        selectionType="multi"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        columnDefinitions={[
          {
            id: "name",
            header: t("feeds.column.name"),
            cell: (item) => (
              <Box variant="span" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                <Button variant="inline-link" onClick={() => onEdit(item)}>
                  {item.name}
                </Button>
              </Box>
            ),
            width: columnWidths.name || 180,
            minWidth: 150,
            sortingField: "name",
          },
          ...(isPrimaryAdmin
            ? [
                {
                  id: "creator",
                  header: t("feeds.column.creator"),
                  cell: (item) => item.user_id || "-",
                  width: columnWidths.creator || 120,
                  minWidth: 100,
                  sortingField: "user_id",
                },
              ]
            : []),
          {
            id: "description",
            header: t("feeds.column.description"),
            cell: (item) => (
              <Box variant="span" wordBreak="break-word">
                {item.description || "-"}
              </Box>
            ),
            width: columnWidths.description || 200,
            maxWidth: 250,
            sortingField: "description",
          },
          {
            id: "filters",
            header: t("feeds.column.filters"),
            cell: (item) => {
              const filters = [];
              if (item.filters?.regions?.length) filters.push(`${item.filters.regions.length} ${t("feeds.filter.regions")}`);
              if (item.filters?.services?.length) filters.push(`${item.filters.services.length} ${t("feeds.filter.services")}`);
              if (item.filters?.keywords?.length) filters.push(`${item.filters.keywords.length} ${t("feeds.filter.keywords")}`);
              return filters.join(", ") || t("feeds.filter.none");
            },
            width: columnWidths.filters || 150,
            minWidth: 120,
          },
          {
            id: "delivery",
            header: t("feeds.column.delivery"),
            cell: (item) => {
              const hasSlack = item.delivery?.channel_name || item.delivery?.slack_webhook || item.delivery?.slack_channel_id;
              const hasWebhook = item.delivery?.slack_webhook || item.delivery?.slack_channel_id;

              return (
                <SpaceBetween direction="vertical" size="xxs">
                  <SpaceBetween direction="horizontal" size="xs">
                    {item.rss_url ? (
                      <>
                        {/* nosemgrep: react-href-var */}
                        {/* URLs come from our backend API, not user input */}
                        <a href={item.rss_url} target="_blank" rel="noopener noreferrer">
                          {t("feeds.link.rss")}
                        </a>
                        <span>|</span>
                        {/* nosemgrep: react-href-var */}
                        <a href={item.html_url} target="_blank" rel="noopener noreferrer">
                          {t("feeds.link.html")}
                        </a>
                      </>
                    ) : (
                      <span>{t("feeds.delivery.rss")}</span>
                    )}
                  </SpaceBetween>
                  {hasSlack && (
                    <Box variant="span">
                      {hasWebhook
                        ? item.delivery?.channel_name || t("feeds.delivery.slack")
                        : `${item.delivery?.channel_name || t("feeds.delivery.slack")} (${t("feeds.delivery.pending")})`}
                    </Box>
                  )}
                </SpaceBetween>
              );
            },
            width: columnWidths.delivery || 140,
            minWidth: 120,
          },
          {
            id: "enabled",
            header: t("feeds.column.status"),
            cell: (item) => <Badge color={item.enabled ? "green" : "grey"}>{item.enabled ? t("feeds.status.enabled") : t("feeds.status.disabled")}</Badge>,
            width: columnWidths.enabled || 100,
            minWidth: 90,
            sortingField: "enabled",
          },
          {
            id: "news_items_sent",
            header: t("feeds.column.news-items-sent"),
            cell: (item) => (item.news_items_sent || 0).toLocaleString(),
            width: columnWidths.news_items_sent || 120,
            minWidth: 100,
            sortingField: "news_items_sent",
          },
          {
            id: "updated_at",
            header: t("feeds.column.last-updated"),
            cell: (item) => {
              if (!item.updated_at) return "-";
              const date = new Date(item.updated_at);
              const day = String(date.getDate()).padStart(2, "0");
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = String(date.getFullYear()).slice(-2);
              return `${day}/${month}/${year}`;
            },
            width: columnWidths.updated_at || 100,
            minWidth: 90,
            sortingField: "updated_at",
          },
        ]}
        items={paginatedFeeds}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={handleSortingChange}
        resizableColumns
        onColumnWidthsChange={handleColumnWidthsChange}
        filter={
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder={t("feeds.action.search")}
            onChange={({ detail }) => setFilteringText(detail.filteringText)}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>{t("feeds.list.no-feeds")}</b>
              <Button onClick={onRefresh}>{t("feeds.list.refresh")}</Button>
            </SpaceBetween>
          </Box>
        }
        header={
          <Header
            counter={`(${sortedFeeds.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={onRefresh} disabled={actionInProgress}>
                  {t("feeds.list.refresh")}
                </Button>
                <ButtonDropdown
                  items={[
                    {
                      text: t("feeds.action.edit"),
                      id: "edit",
                      disabled: selectedItems.length !== 1,
                    },
                    {
                      text: t("feeds.action.enable"),
                      id: "enable",
                      disabled: selectedItems.length === 0,
                    },
                    {
                      text: t("feeds.action.disable"),
                      id: "disable",
                      disabled: selectedItems.length === 0,
                    },
                    {
                      text: t("feeds.action.regenerate"),
                      id: "regenerate",
                      disabled: selectedItems.length === 0,
                    },
                    {
                      text: t("feeds.action.test-slack"),
                      id: "test-slack",
                      disabled: (() => {
                        const disabled =
                          selectedItems.length !== 1 || !(selectedItems[0]?.delivery?.slack_webhook || selectedItems[0]?.delivery?.slack_channel_id);
                        if (selectedItems.length === 1) {
                          console.log("Test Slack button check:", {
                            feed: selectedItems[0]?.name,
                            hasWebhook: !!selectedItems[0]?.delivery?.slack_webhook,
                            hasChannelId: !!selectedItems[0]?.delivery?.slack_channel_id,
                            disabled,
                          });
                        }
                        return disabled;
                      })(),
                    },
                    {
                      text: t("feeds.action.delete"),
                      id: "delete",
                      disabled: selectedItems.length === 0,
                    },
                  ]}
                  onItemClick={({ detail }) => {
                    switch (detail.id) {
                      case "edit":
                        onEdit(selectedItems[0]);
                        break;
                      case "enable":
                        handleEnableDisable(true);
                        break;
                      case "disable":
                        handleEnableDisable(false);
                        break;
                      case "regenerate":
                        handleRegenerateMultiple();
                        break;
                      case "test-slack":
                        handleTestSlack();
                        break;
                      case "delete":
                        handleDeleteClick();
                        break;
                    }
                  }}
                  disabled={actionInProgress}
                  loading={actionInProgress}
                >
                  {t("feeds.list.actions")}
                </ButtonDropdown>
              </SpaceBetween>
            }
          >
            {t("feeds.list.title")}
          </Header>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={Math.ceil(sortedFeeds.length / pageSize)}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        }
        preferences={
          <CollectionPreferences
            title={t("feeds.preferences.title")}
            confirmLabel={t("feeds.preferences.confirm")}
            cancelLabel={t("feeds.preferences.cancel")}
            preferences={{
              pageSize: pageSize,
            }}
            pageSizePreference={{
              title: t("feeds.preferences.page-size"),
              options: [
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 50, label: "50" },
                { value: 100, label: "100" },
              ],
            }}
            onConfirm={handlePreferencesConfirm}
          />
        }
      />
    </>
  );
}
